import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import Login from './components/Login';
import TopNav from './components/TopNav';
import Dashboard from './components/Dashboard';
import TopicQueue from './components/TopicQueue';
import KeywordIntelligence from './components/KeywordIntelligence';
import KeywordComparison from './components/KeywordComparison';
import GeographicOptimizer from './components/GeographicOptimizer';
import MultiLanguageTrends from './components/MultiLanguageTrends';
import CompetitorAnalysis from './components/CompetitorAnalysis';
import RankAbove from './components/RankAbove';
import TrendDiscovery from './components/TrendDiscovery';
import AISearchOptimizer from './components/AISearchOptimizer';
import WordPressSites from './components/WordPressSites';
import Background from './components/Background';
import ErrorBoundary from './components/ErrorBoundary';
import QuickPost from './components/QuickPost';
import Onboarding from './components/Onboarding';
import IntentPickerModal from './components/IntentPickerModal';
import { ContentVertexProvider, useContentVertex } from './contexts/ContentVertexContext';
import './App.css';

// Handles the onboarding redirect check once profile is loaded
function OnboardingGuard({ children }) {
  const { onboardingComplete, profileLoaded, setOnboardingComplete, setNiche } = useContentVertex();

  if (!profileLoaded) return null;

  if (!onboardingComplete) {
    return (
      <Onboarding
        onComplete={(selectedNiche) => {
          setNiche(selectedNiche);
          setOnboardingComplete(true);
        }}
      />
    );
  }

  return children;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQuickPost, setShowQuickPost] = useState(false);
  const [showIntentPicker, setShowIntentPicker] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      setShowIntentPicker(true);
    }
    setLoading(false);
  }, []);

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    setShowIntentPicker(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!user) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <ErrorBoundary>
    <div className="relative">
      <Background />
      <div className="relative z-10">
        <BrowserRouter>
          <ContentVertexProvider onOpenQuickPost={() => setShowQuickPost(true)}>
            <OnboardingGuard>
              <TopNav user={user} />
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/topic-queue" element={<TopicQueue />} />
                <Route path="/competitor-analysis" element={<CompetitorAnalysis user={user} />} />
                <Route path="/rank-above" element={<RankAbove user={user} />} />
                <Route path="/trend-discovery" element={<TrendDiscovery user={user} />} />

                <Route path="/analytics" element={<Dashboard />} />
                <Route path="/keyword-intelligence" element={<KeywordIntelligence user={user} />} />
                <Route path="/keyword-comparison" element={<KeywordComparison user={user} />} />
                <Route path="/geographic-optimizer" element={<GeographicOptimizer user={user} />} />
                <Route path="/multilanguage-trends" element={<MultiLanguageTrends user={user} />} />
                <Route path="/ai-search-optimizer" element={<AISearchOptimizer user={user} />} />
                <Route path="/wordpress-sites" element={<WordPressSites user={user} />} />
                <Route path="*" element={<Dashboard />} />
              </Routes>
              {showQuickPost && (
                <QuickPost onClose={() => setShowQuickPost(false)} />
              )}
              {showIntentPicker && (
                <IntentPickerModal onDismiss={() => setShowIntentPicker(false)} />
              )}
            </OnboardingGuard>
          </ContentVertexProvider>
        </BrowserRouter>
      </div>
    </div>
    </ErrorBoundary>
  );
}

export default App;
